// frontend/src/site/components/SiteHero.jsx
import React, { useMemo } from 'react';
import { useSite } from '../SiteContext';
import {
  headingClasses,
  mutedTextClasses,
  buttonClasses,
} from '../theme/siteStyles';

const cx = (...c) => c.filter(Boolean).join(' ');

const FALLBACK_COVER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900">
    <defs>
      <linearGradient id="g" x1="0" x2="1">
        <stop stop-color="#0f172a"/>
        <stop offset="1" stop-color="#111827"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#g)"/>
  </svg>`);

const FALLBACK_AVATAR =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <defs>
      <linearGradient id="a" x1="0" x2="1">
        <stop stop-color="#e5e7eb"/>
        <stop offset="1" stop-color="#f3f4f6"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#a)"/>
    <circle cx="150" cy="120" r="55" fill="#cbd5e1"/>
    <rect x="65" y="190" width="170" height="80" rx="40" fill="#cbd5e1"/>
  </svg>`);

/**
 * SiteHero (compatível + builder-ready)
 *
 * NOVAS PROPS (builder):
 * - titulo, subtitulo, frase
 * - ctaPrimario, ctaSecundario, mostrarCtaSecundario
 * - nome, fotoUrl, capaUrl, whatsappUrl
 *
 * ANTIGAS PROPS (mantidas):
 * - title, subtitle, coverUrl, showCta, ctaLabel, onCta
 */
export default function SiteHero({
  children,

  // legado
  title,
  subtitle,
  coverUrl,
  showCta = false,
  ctaLabel = 'Ver imóveis',
  onCta,

  // builder (novo)
  titulo,
  subtitulo,
  frase,
  ctaPrimario,
  ctaSecundario,
  mostrarCtaSecundario = true,

  // dados do corretor
  nome,
  fotoUrl,
  capaUrl,
  whatsappUrl,

  heightClass = 'min-h-[72svh] md:min-h-[78svh]',
}) {
  const { perfilSite, theme, ownerWhatsUrl } = useSite() || {};
  const isDark = (theme?.mode || 'light') === 'dark';

  const cover = useMemo(() => {
    return (
      capaUrl ||
      coverUrl ||
      perfilSite?.siteCoverUrl ||
      perfilSite?.perfil?.coverUrl ||
      perfilSite?.coverUrl ||
      FALLBACK_COVER
    );
  }, [capaUrl, coverUrl, perfilSite]);

  const avatar = useMemo(() => {
    return (
      fotoUrl ||
      perfilSite?.fotoPerfilUrl ||
      perfilSite?.fotoPerfil?.url ||
      perfilSite?.fotoPerfil ||
      FALLBACK_AVATAR
    );
  }, [fotoUrl, perfilSite]);

  const finalNome =
    nome || perfilSite?.nome || perfilSite?.siteHandle || 'Seu site';

  // Builder tem prioridade; se não tiver, cai no legado; se não tiver, cai no perfil
  const finalTitulo =
    (titulo && String(titulo).trim()) ||
    (title && String(title).trim()) ||
    finalNome;

  const finalSubtitulo =
    (subtitulo && String(subtitulo).trim()) ||
    (subtitle && String(subtitle).trim()) ||
    perfilSite?.bio ||
    perfilSite?.perfil?.bio ||
    '';

  const finalFrase = (frase && String(frase).trim()) || '';

  const wa = whatsappUrl || ownerWhatsUrl || '';

  function handlePrimaryCta() {
    // Se veio handler legado, usa ele
    if (typeof onCta === 'function') return onCta();

    // senão, padrão builder: WhatsApp
    if (wa) window.open(wa, '_blank');
  }

  return (
    <header className="relative">
      <div className={cx('relative w-full overflow-hidden', heightClass)}>
        {/* Capa */}
        <img
          src={cover}
          alt="Capa do site"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          decoding="async"
        />

        {/* Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? 'linear-gradient(to bottom, rgba(0,0,0,0.60), rgba(0,0,0,0.25), rgba(0,0,0,0.65))'
              : 'linear-gradient(to bottom, rgba(15,23,42,0.42), rgba(15,23,42,0.10), rgba(15,23,42,0.40))',
          }}
        />

        {/* Conteúdo central */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-center">
          <div className="max-w-2xl">
            {/* Avatar + Nome */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border border-white/25 bg-white/10">
                <img
                  src={avatar}
                  alt={finalNome}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div>
                <div className="text-white/80 text-xs md:text-sm">
                  {finalNome}
                </div>
                {perfilSite?.creci ? (
                  <div className="text-white/70 text-xs">
                    CRECI: {perfilSite.creci}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Título */}
            <h1
              className={cx(
                headingClasses(theme, 'h1'),
                'text-white drop-shadow'
              )}
            >
              {finalTitulo}
            </h1>

            {/* Subtítulo */}
            {finalSubtitulo ? (
              <p
                className={cx(
                  'mt-4 text-white/90 text-base md:text-lg drop-shadow'
                )}
              >
                {finalSubtitulo}
              </p>
            ) : null}

            {/* Frase extra */}
            {finalFrase ? (
              <p className={cx('mt-3 text-white/80 text-sm md:text-base')}>
                {finalFrase}
              </p>
            ) : null}

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap gap-2">
              {/* Novo CTA primário (builder) OU legado (showCta) */}
              {ctaPrimario || showCta ? (
                <button
                  type="button"
                  onClick={handlePrimaryCta}
                  className={cx(
                    buttonClasses(theme, 'primary'),
                    '!bg-white !text-slate-900'
                  )}
                >
                  {ctaPrimario || ctaLabel}
                </button>
              ) : null}

              {/* CTA secundário (builder) */}
              {mostrarCtaSecundario && ctaSecundario ? (
                <a
                  href="#imoveis"
                  className={cx(
                    buttonClasses(theme, 'secondary'),
                    'inline-flex !border-white/30 !text-white hover:!bg-white/10'
                  )}
                >
                  {ctaSecundario}
                </a>
              ) : null}
            </div>
          </div>

          {/* Slot (ex.: SiteFilterBar) fixado embaixo do hero */}
          {children && (
            <div className="mt-10 md:mt-14">
              <div className={cx(mutedTextClasses(), 'sr-only')}>
                Filtros de busca
              </div>
              {children}
            </div>
          )}
        </div>

        {/* Fade pro fundo do site */}
        <div
          className="absolute inset-x-0 bottom-0 h-24"
          style={{
            background: isDark
              ? 'linear-gradient(to top, var(--bg), rgba(0,0,0,0))'
              : 'linear-gradient(to top, var(--bg), rgba(255,255,255,0))',
          }}
          aria-hidden
        />
      </div>
    </header>
  );
}
